import { useCallback, useMemo, useState, type ReactElement } from "react";
import { Search } from "lucide-react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type { NotificationItem } from "@playbookai/types";
import {
  Alert,
  AsyncSection,
  asyncStatus,
  Button,
  EmptyState,
  Input,
  Pagination,
  Select,
  Skeleton,
  useCursorPagination,
} from "@playbookai/ui";
import { useShellBridge } from "@playbookai/api-clients";
import {
  useMarkAllNotificationsReadAction,
  useMarkNotificationReadAction,
  useNotificationInboxQuery,
  useUnreadCountQuery,
} from "../notifications/useNotifications.js";
import {
  useNotificationPreferencesQuery,
  useSetAllNotificationPreferences,
} from "../notifications/useNotificationPreferences.js";
import { useOpenNotification } from "../notifications/useOpenNotification.js";
import { notificationHref } from "../notifications/notification-target.js";
import { SETTINGS_SECTION_ROUTES } from "../routes.js";
import {
  previousDay,
  useDateTimeFormatter,
} from "../settings/format-date-time.js";
import { NotificationFeedList } from "./NotificationFeedList.js";
import {
  InboxFilters,
  InboxSummary,
  type DeliveryQuickControls,
  type NotificationView,
} from "./NotificationInboxPanels.js";
import {
  categoryOf,
  matchesNotificationSearch,
  NOTIFICATION_CATEGORIES,
} from "./notification-presentation.js";
import { getEnabledModules } from "../modules/index.js";
import { useAuthStore } from "../auth/auth-store.js";

const INBOX_PAGE_SIZES = [25, 50] as const;
type InboxPageSize = (typeof INBOX_PAGE_SIZES)[number];

export function Notifications(): ReactElement {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const formatter = useDateTimeFormatter();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const view: NotificationView =
    params.get("filter") === "unread" ? "unread" : "all";
  const categoryParam = params.get("category");
  const category =
    NOTIFICATION_CATEGORIES.find((option) => option.value === categoryParam)
      ?.value ?? null;
  // ADR-0100: the session's available list, so a module turned off for this
  // workspace is not offered — installed alone would still list it.
  const tenantEnabledModules = useAuthStore(
    (state) => state.claims?.enabledModules,
  );
  const moduleOptions = useMemo(
    () => [
      { value: "platform", label: "Platform" },
      ...getEnabledModules(tenantEnabledModules).map((module) => ({
        value: module.id,
        label: module.label,
      })),
    ],
    [tenantEnabledModules],
  );
  // An old link may name a module that is now off: its notifications are not
  // served, so the filter falls back to every module rather than an empty list.
  const moduleParam = params.get("module");
  const moduleId = moduleOptions.some((option) => option.value === moduleParam)
    ? moduleParam
    : null;
  const query = params.get("q") ?? "";
  const pageSize = parsePageSize(params.get("pageSize"));
  const serverFilters = useMemo(
    () => ({
      ...(view === "unread" ? { unread: true } : {}),
      ...(category ? { class: category } : {}),
      ...(moduleId ? { moduleId } : {}),
    }),
    [category, moduleId, view],
  );
  const paging = useCursorPagination(
    JSON.stringify({ filters: serverFilters, pageSize }),
    {
      pageSize,
    },
  );
  const feed = useNotificationInboxQuery(
    serverFilters,
    paging.cursor,
    pageSize,
  );
  const count = useUnreadCountQuery();
  const preferences = useNotificationPreferencesQuery();
  const bulkPreferences = useSetAllNotificationPreferences();
  const items = feed.data?.items ?? [];
  const markRead = useMarkNotificationReadAction();
  const markAllRead = useMarkAllNotificationsReadAction();
  const { openModuleEntityTab } = useShellBridge();
  const openNotification = useOpenNotification(openModuleEntityTab);
  // A row's link, for a new browser tab or a copied link (d1).
  const resolveHref = useCallback(
    (href: string, eventType?: string): string =>
      notificationHref(
        href,
        getEnabledModules(tenantEnabledModules).map((module) => module.id),
        eventType,
      ),
    [tenantEnabledModules],
  );
  const unreadOnPage = items.filter((item) => item.unread).length;
  const hasUnloadedRows = Boolean(feed.data?.nextCursor);
  // Unknown, not zero, while the total has not loaded: a read that failed or
  // has not answered supports no "All caught up" (PB-5337). The page's own
  // count stands in only when it is exact — the first and only page, with no
  // class or module filter. Any other page's count is a lower bound, so it is
  // never shown as the total; it only proves there is something to mark read.
  // Nor is a placeholder: while a cleared filter's page is still loading the
  // query keeps showing the previous, filtered page, whose count is not exact.
  const pageIsWholeFeed =
    paging.page === 1 && !hasUnloadedRows && !category && !moduleId;
  const unreadCount =
    count.data ??
    (feed.data && !feed.isPlaceholderData && pageIsWholeFeed
      ? unreadOnPage
      : null);
  const hasUnread = unreadCount === null ? unreadOnPage > 0 : unreadCount > 0;

  const visible = items.filter((item) => {
    if (view === "unread" && !item.unread) return false;
    if (category && categoryOf(item) !== category) return false;
    if (moduleId && moduleValue(item) !== moduleId) return false;
    return matchesNotificationSearch(item, query);
  });
  const groups = useMemo(
    () => groupNotifications(visible, formatter),
    [visible, formatter],
  );
  const hasActiveFilters = Boolean(
    category || moduleId || query || view === "unread",
  );
  const readError =
    items.length > 0 ? (feed.error ?? count.error ?? null) : null;
  const actionError = markRead.error ?? markAllRead.error;
  const delivery: DeliveryQuickControls = {
    events: preferences.data?.events ?? [],
    isLoading: preferences.isLoading,
    error: preferences.data ? null : preferences.error,
    refreshError: preferences.data ? preferences.error : null,
    savingChannel: bulkPreferences.pendingChannel,
    saveError: bulkPreferences.error,
    onRetry: () => void preferences.refetch(),
    onDismissError: bulkPreferences.reset,
    onSetAll: (channel, enabled) =>
      bulkPreferences.setAll(preferences.data?.events ?? [], channel, enabled),
  };

  const setParam = (key: string, value: string | null): void => {
    setParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (!value) next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true },
    );
  };
  const clearFilters = (): void =>
    setParams(
      pageSize === INBOX_PAGE_SIZES[0] ? {} : { pageSize: String(pageSize) },
      { replace: true },
    );
  const refresh = (): void => {
    setIsRefreshing(true);
    void Promise.all([feed.refetch(), count.refetch()]).finally(() =>
      setIsRefreshing(false),
    );
  };

  return (
    /* The page is its own size container, as wide as main (PB-5436): the
       Player AI side rail narrows main without changing the window, so the
       three-column inbox follows this width, not the window. Padding-free,
       so `xl` maps exactly to 80rem (minus the 1rem scrollbar allowance). */
    <div className="@container/notifications">
      <div className="pb-motion-content-enter mx-auto max-w-[90rem] px-4 pt-3 pb-6 sm:px-6 lg:px-8">
        {/* Kept hand-built: SectionHeader has no baseline-aligned meta beside a
            heading-1 on a 12px hairline; its rows are centred with mb-6/pb-4.5,
            a look change no PR 3 row approves. */}
        <header className="mb-3 border-b border-pb-border/70 pb-3">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h1 className="text-heading-1 text-pb-text">Inbox</h1>
            <p className="text-body-small text-pb-text-secondary">
              {/* The "across N" suffix rides on the count: with the total unknown
                  it would read as a sentence with no subject (PB-5337). */}
              {unreadCount !== null ? (
                <>
                  <span>
                    {unreadCount > 0
                      ? `${unreadCount} unread`
                      : "All caught up"}
                  </span>
                  {items.length > 0
                    ? ` across ${items.length} recent notification${items.length === 1 ? "" : "s"}`
                    : ""}
                </>
              ) : null}
            </p>
          </div>
          {hasUnloadedRows ? (
            <p className="mt-1 text-dense-1 text-pb-text-muted">
              Showing the most recent {items.length}; more matching
              notifications are available.
            </p>
          ) : null}
        </header>

        <div className="grid min-w-0 gap-6 @min-[79rem]/notifications:grid-cols-[11rem_minmax(0,1fr)_18.5rem] @min-[79rem]/notifications:gap-6">
          <InboxFilters
            moduleOptions={moduleOptions}
            view={view}
            category={category}
            moduleId={moduleId}
            onViewChange={(next) =>
              setParam("filter", next === "all" ? null : next)
            }
            onCategoryChange={(next) => setParam("category", next)}
            onModuleChange={(next) => setParam("module", next)}
          />

          <main className="min-w-0">
            <div className="mb-4">
              <Input
                type="search"
                aria-label="Search notifications"
                placeholder="Search this page"
                value={query}
                onChange={(event) => setParam("q", event.target.value)}
                prefix={<Search className="h-4 w-4" aria-hidden />}
              />
              <div className="mt-2 flex min-h-6 items-center justify-between gap-3 text-dense-1 text-pb-text-muted">
                <span>
                  Showing {visible.length} of {items.length} on this page
                </span>
                {hasActiveFilters ? (
                  <Button variant="link" size="sm" onClick={clearFilters}>
                    Show all notifications
                  </Button>
                ) : null}
              </div>
            </div>

            {readError ? (
              <Alert
                tone="critical"
                title="Some notifications couldn't refresh"
                className="mb-4"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <span>{readError.message}</span>
                  <Button variant="ghost" size="sm" onClick={refresh}>
                    Try again
                  </Button>
                </div>
              </Alert>
            ) : null}

            <AsyncSection
              status={asyncStatus({
                isLoading: feed.isLoading,
                isError: feed.isError,
                isEmpty: feed.isSuccess && items.length === 0,
                hasData: items.length > 0,
              })}
              loadingLabel="notifications"
              errorTitle="Couldn't load your notifications"
              errorDescription={feed.error?.message}
              onRetry={() => void feed.refetch()}
              skeleton={<FeedSkeleton />}
              empty={
                <EmptyState
                  {...(hasActiveFilters
                    ? { variant: "no-results" as const }
                    : {})}
                  title={
                    view === "unread" && !category && !moduleId && !query
                      ? "No unread notifications"
                      : hasActiveFilters
                        ? "No notifications match"
                        : "No notifications yet"
                  }
                  description={
                    hasActiveFilters
                      ? "Try another view, category, module, or search."
                      : "New activity will appear here when it happens."
                  }
                  action={
                    hasActiveFilters ? (
                      <Button variant="ghost" size="sm" onClick={clearFilters}>
                        Show all notifications
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        size="sm"
                        loading={isRefreshing}
                        onClick={refresh}
                      >
                        Refresh
                      </Button>
                    )
                  }
                />
              }
            >
              {visible.length === 0 ? (
                <EmptyState
                  variant="no-results"
                  title={
                    view === "unread" && !category && !moduleId && !query
                      ? "No unread notifications"
                      : "No notifications match"
                  }
                  description="Try another view, category, module, or search."
                  action={
                    hasActiveFilters ? (
                      <Button variant="ghost" size="sm" onClick={clearFilters}>
                        Show all notifications
                      </Button>
                    ) : undefined
                  }
                />
              ) : (
                <NotificationFeedList
                  groups={groups}
                  /* Changing the question replaces the answer; only a row the
                     reader removed from THIS set leaves on the exit grade
                     (ADR-0091). */
                  viewKey={`${view}|${category ?? ""}|${moduleId ?? ""}|${query}|${paging.page}`}
                  formatter={formatter}
                  onMarkRead={markRead.markRead}
                  onOpen={openNotification}
                  resolveHref={resolveHref}
                />
              )}
            </AsyncSection>
            {items.length > 0 ? (
              <div className="mt-4 flex flex-wrap items-center justify-end gap-3">
                <Select
                  ariaLabel="Notifications per page"
                  value={String(pageSize)}
                  options={INBOX_PAGE_SIZES.map((size) => ({
                    value: String(size),
                    label: `${size} per page`,
                  }))}
                  onChange={(value) =>
                    setParam(
                      "pageSize",
                      value === String(INBOX_PAGE_SIZES[0]) ? null : value,
                    )
                  }
                  className="w-36"
                />
                {paging.page > 1 || feed.data?.nextCursor ? (
                  <Pagination
                    mode="cursor"
                    page={paging.page}
                    pageItemCount={items.length}
                    canGoBack={paging.canGoBack}
                    hasNext={Boolean(feed.data?.nextCursor)}
                    busy={feed.isFetching}
                    onPrev={paging.goBack}
                    onNext={() => {
                      const cursor = feed.data?.nextCursor;
                      if (cursor) paging.goNext(cursor);
                    }}
                  />
                ) : null}
              </div>
            ) : null}
          </main>

          <InboxSummary
            unreadCount={unreadCount}
            hasUnread={hasUnread}
            isRefreshing={isRefreshing}
            isMarkingAllRead={markAllRead.isPending}
            actionError={actionError}
            delivery={delivery}
            onRefresh={refresh}
            onMarkAllRead={() =>
              markAllRead.markAllRead(feed.data?.responseReceivedAt)
            }
            onDismissActionError={() => {
              markRead.reset();
              markAllRead.reset();
            }}
            onOpenSettings={() =>
              navigate(SETTINGS_SECTION_ROUTES["notifications"]!)
            }
          />
        </div>
      </div>
    </div>
  );
}

function moduleValue(item: NotificationItem): string {
  return item.moduleId ?? "platform";
}

function parsePageSize(value: string | null): InboxPageSize {
  const parsed = Number(value);
  return INBOX_PAGE_SIZES.includes(parsed as InboxPageSize)
    ? (parsed as InboxPageSize)
    : INBOX_PAGE_SIZES[0];
}

function groupNotifications(
  items: readonly NotificationItem[],
  formatter: ReturnType<typeof useDateTimeFormatter>,
): { key: string; label: string; items: NotificationItem[] }[] {
  const today = formatter.isoDate(Date.now());
  const yesterday = previousDay(today);
  const groups = new Map<string, NotificationItem[]>();
  for (const item of items) {
    const day = formatter.isoDate(item.ts);
    const key =
      day === today ? "today" : day === yesterday ? "yesterday" : "earlier";
    const group = groups.get(key) ?? [];
    group.push(item);
    groups.set(key, group);
  }
  return ["today", "yesterday", "earlier"].flatMap((key) => {
    const groupedItems = groups.get(key);
    if (!groupedItems) return [];
    const label =
      key === "today" ? "Today" : key === "yesterday" ? "Yesterday" : "Earlier";
    return [{ key, label, items: groupedItems }];
  });
}

function FeedSkeleton(): ReactElement {
  return (
    <div className="flex flex-col gap-3" role="presentation">
      <Skeleton shape="card" className="h-28" />
      <Skeleton shape="card" className="h-28" />
      <Skeleton shape="card" className="h-28" />
    </div>
  );
}
